import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SlDoc } from 'react-icons/sl';
import { LuFileAudio2 } from 'react-icons/lu';

interface DropZoneOverlayProps {
  /** 当文档文件被拖放时的回调 */
  onDocumentDrop: (files: File[]) => void;
  /** 当音频文件被拖放时的回调 */
  onAudioDrop: (files: File[]) => void;
  /** 禁用状态 */
  disabled?: boolean;
}

// 支持的文件格式常量
const DOCUMENT_EXTENSIONS = ['.docx'];
const AUDIO_EXTENSIONS = ['.wav', '.m4a', '.aac', '.mp3', '.ogg'];

/**
 * 拖拽文件上传浮层组件
 * 当用户拖拽文件到浏览器时，显示两个大的区域分别用于上传文档和音频
 */
const DropZoneOverlay: React.FC<DropZoneOverlayProps> = ({
  onDocumentDrop,
  onAudioDrop,
  disabled = false,
}) => {
  // 是否正在拖拽文件到页面
  const [isDragging, setIsDragging] = useState(false);
  // 当前悬停的区域：'document' | 'audio' | null
  const [hoverZone, setHoverZone] = useState<'document' | 'audio' | null>(null);
  
  // 使用 ref 来追踪浮层是否已显示，避免事件循环
  const overlayRef = useRef<HTMLDivElement>(null);
  // 用于延迟隐藏浮层的定时器
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 检查文件是否为文档类型
  const isDocumentFile = useCallback((file: File) => {
    const name = file.name.toLowerCase();
    return DOCUMENT_EXTENSIONS.some(ext => name.endsWith(ext));
  }, []);

  // 检查文件是否为音频类型
  const isAudioFile = useCallback((file: File) => {
    const name = file.name.toLowerCase();
    return AUDIO_EXTENSIONS.some(ext => name.endsWith(ext));
  }, []);

  // 清除隐藏定时器
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  // 处理拖拽进入（window 级别）
  const handleWindowDragEnter = useCallback((e: DragEvent) => {
    // 只处理文件拖拽
    if (!e.dataTransfer?.types.includes('Files')) return;
    if (disabled) return;
    
    e.preventDefault();
    clearHideTimeout();
    setIsDragging(true);
  }, [disabled, clearHideTimeout]);

  // 处理拖拽经过（window 级别，必须阻止默认行为）
  const handleWindowDragOver = useCallback((e: DragEvent) => {
    if (!e.dataTransfer?.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  }, []);

  // 处理拖拽离开（window 级别）
  const handleWindowDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    
    // 使用 relatedTarget 检查是否真正离开了 window
    // 如果 relatedTarget 为 null，说明离开了浏览器窗口
    if (e.relatedTarget === null) {
      // 使用短延时来避免闪烁（因为可能马上又进入）
      clearHideTimeout();
      hideTimeoutRef.current = setTimeout(() => {
        setIsDragging(false);
        setHoverZone(null);
      }, 50);
    }
  }, [clearHideTimeout]);

  // 处理放下（window 级别，处理在非区域放下的情况）
  const handleWindowDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    clearHideTimeout();
    setIsDragging(false);
    setHoverZone(null);
  }, [clearHideTimeout]);

  // 添加/移除 window 级别事件监听
  useEffect(() => {
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);
    
    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('drop', handleWindowDrop);
      clearHideTimeout();
    };
  }, [handleWindowDragEnter, handleWindowDragOver, handleWindowDragLeave, handleWindowDrop, clearHideTimeout]);

  // 浮层的拖拽事件处理（阻止事件冒泡到 window）
  const handleOverlayDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleOverlayDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // 检查是否离开了浮层区域
    const rect = overlayRef.current?.getBoundingClientRect();
    if (rect) {
      const { clientX, clientY } = e;
      if (
        clientX <= rect.left ||
        clientX >= rect.right ||
        clientY <= rect.top ||
        clientY >= rect.bottom
      ) {
        clearHideTimeout();
        hideTimeoutRef.current = setTimeout(() => {
          setIsDragging(false);
          setHoverZone(null);
        }, 50);
      }
    }
  }, [clearHideTimeout]);

  // 区域的拖拽事件处理
  const handleZoneDragEnter = useCallback((zone: 'document' | 'audio') => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearHideTimeout();
    setHoverZone(zone);
  }, [clearHideTimeout]);

  const handleZoneDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearHideTimeout();
  }, [clearHideTimeout]);

  const handleZoneDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // 不要在这里清除 hoverZone，让 dragEnter 来设置正确的区域
  }, []);

  // 处理区域内的放下
  const handleZoneDrop = useCallback((zone: 'document' | 'audio') => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    clearHideTimeout();
    
    const files = Array.from(e.dataTransfer.files);
    console.log('Drop zone:', zone, 'Files:', files);
    
    if (zone === 'document') {
      const documentFiles = files.filter(file => isDocumentFile(file));
      console.log('Document files:', documentFiles);
      if (documentFiles.length > 0) {
        onDocumentDrop(documentFiles);
      }
    } else {
      const audioFiles = files.filter(file => isAudioFile(file));
      console.log('Audio files:', audioFiles);
      if (audioFiles.length > 0) {
        onAudioDrop(audioFiles);
      }
    }
    
    // 重置状态
    setIsDragging(false);
    setHoverZone(null);
  }, [isDocumentFile, isAudioFile, onDocumentDrop, onAudioDrop, clearHideTimeout]);

  // 处理点击浮层背景关闭
  const handleOverlayClick = useCallback(() => {
    clearHideTimeout();
    setIsDragging(false);
    setHoverZone(null);
  }, [clearHideTimeout]);

  // 不显示浮层
  if (!isDragging) return null;

  const overlayContent = (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center gap-8 p-8"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={handleOverlayClick}
      onDragOver={handleOverlayDragOver}
      onDragLeave={handleOverlayDragLeave}
    >
      {/* 文档上传区域 */}
      <div
        className={`
          flex-1 max-w-md h-64 rounded-2xl border-4 border-dashed
          flex flex-col items-center justify-center gap-4 cursor-pointer
          transition-all duration-200 ease-in-out
          ${hoverZone === 'document' 
            ? 'border-blue-500 bg-blue-50 scale-105 shadow-2xl' 
            : 'border-gray-300 bg-white hover:border-blue-300'
          }
        `}
        onDragEnter={handleZoneDragEnter('document')}
        onDragLeave={handleZoneDragLeave}
        onDragOver={handleZoneDragOver}
        onDrop={handleZoneDrop('document')}
        onClick={(e) => e.stopPropagation()}
      >
        <SlDoc 
          className={`text-6xl transition-colors duration-200 pointer-events-none ${
            hoverZone === 'document' ? 'text-blue-500' : 'text-gray-400'
          }`} 
        />
        <div className="text-center px-4 pointer-events-none">
          <p className={`text-xl font-semibold ${
            hoverZone === 'document' ? 'text-blue-600' : 'text-gray-700'
          }`}>
            放开上传文档
          </p>
          <p className="text-sm text-gray-500 mt-2">
            支持格式：docx（最多 2 个文件）
          </p>
        </div>
      </div>

      {/* 音频上传区域 */}
      <div
        className={`
          flex-1 max-w-md h-64 rounded-2xl border-4 border-dashed
          flex flex-col items-center justify-center gap-4 cursor-pointer
          transition-all duration-200 ease-in-out
          ${hoverZone === 'audio' 
            ? 'border-green-500 bg-green-50 scale-105 shadow-2xl' 
            : 'border-gray-300 bg-white hover:border-green-300'
          }
        `}
        onDragEnter={handleZoneDragEnter('audio')}
        onDragLeave={handleZoneDragLeave}
        onDragOver={handleZoneDragOver}
        onDrop={handleZoneDrop('audio')}
        onClick={(e) => e.stopPropagation()}
      >
        <LuFileAudio2 
          className={`text-6xl transition-colors duration-200 pointer-events-none ${
            hoverZone === 'audio' ? 'text-green-500' : 'text-gray-400'
          }`} 
        />
        <div className="text-center px-4 pointer-events-none">
          <p className={`text-xl font-semibold ${
            hoverZone === 'audio' ? 'text-green-600' : 'text-gray-700'
          }`}>
            放开上传音频
          </p>
          <p className="text-sm text-gray-500 mt-2">
            支持格式：wav / m4a / aac / mp3 / ogg（最多 1 个文件）
          </p>
        </div>
      </div>
    </div>
  );

  // 使用 Portal 渲染到 body，确保浮层在最上层
  return createPortal(overlayContent, document.body);
};

export default DropZoneOverlay;
